// ============================================================================
// Modal "Genera Report Social" — usa il TEMPLATE UFFICIALE BLOCCATO ByMarke.
// Il layout del PDF è definitivo e non modificabile: da qui si scelgono solo
// il cliente (implicito) e il periodo, in DUE sole modalità:
//   1. Mensile        → si sceglie il mese
//   2. Personalizzato → si scelgono le date da/a
// ============================================================================

import React, { useState } from 'react';
import { AnimatedModal } from './AnimatedComponents';
import Button from './ui/Button';
import { FileText, Calendar, CalendarRange, ExternalLink, Copy, Check, Loader2 } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clienteId: string;
  clienteNome: string;
  // Se true renderizza il contenuto direttamente nella pagina (vista interna
  // della dashboard, es. pagina Clienti) invece che dentro un modal.
  inline?: boolean;
  // Notifica il genitore quando un report è stato generato con successo
  // (usato per ricaricare la sezione "Report passati").
  onGenerated?: () => void;
}

type Mode = 'monthly' | 'custom';

function prevMonth(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function yesterdayISO(): string {
  const d = new Date(Date.now() - 86_400_000);
  return d.toISOString().slice(0, 10);
}

const ReportGeneratorModal: React.FC<Props> = ({ isOpen, onClose, clienteId, clienteNome, inline = false, onGenerated }) => {
  const [mode, setMode] = useState<Mode>('monthly');
  const [month, setMonth] = useState<string>(prevMonth());
  const [since, setSince] = useState<string>('');
  const [until, setUntil] = useState<string>(yesterdayISO());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => { setError(null); setResultUrl(null); setCopied(false); };

  const generate = async () => {
    reset();
    if (mode === 'custom' && (!since || !until)) { setError('Seleziona entrambe le date.'); return; }
    setLoading(true);
    try {
      const body = mode === 'monthly'
        ? { clientId: clienteId, mode, month }
        : { clientId: clienteId, mode, since, until };
      const res = await fetch('/api/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setError(json.message || 'Generazione fallita, riprova.');
      } else {
        setResultUrl(json.url);
        onGenerated?.();
      }
    } catch {
      setError('Errore di rete: riprova tra poco.');
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async () => {
    if (!resultUrl) return;
    try { await navigator.clipboard.writeText(resultUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* no-op */ }
  };

  const content = (
      <div className="space-y-5 p-1">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Il report usa il <b>template ufficiale ByMarke</b> (layout fisso). Scegli solo il periodo:
        </p>

        {/* Selettore modalità: SOLO 2 opzioni */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setMode('monthly'); reset(); }}
            className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${mode === 'monthly'
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700'}`}
          >
            <Calendar size={16} /> Mensile
          </button>
          <button
            onClick={() => { setMode('custom'); reset(); }}
            className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${mode === 'custom'
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700'}`}
          >
            <CalendarRange size={16} /> Periodo personalizzato
          </button>
        </div>

        {mode === 'monthly' ? (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Mese del report</label>
            <input
              type="month"
              value={month}
              max={currentMonth()}
              onChange={(e) => { setMonth(e.target.value); reset(); }}
              className="w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
            />
            <p className="mt-1.5 text-xs text-slate-400">Il mese in corso viene calcolato fino a ieri.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Dal</label>
              <input
                type="date" value={since} max={yesterdayISO()}
                onChange={(e) => { setSince(e.target.value); reset(); }}
                className="w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Al</label>
              <input
                type="date" value={until} max={yesterdayISO()}
                onChange={(e) => { setUntil(e.target.value); reset(); }}
                className="w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
              />
              <p className="mt-1.5 text-xs text-slate-400">Massimo ieri · finestra fino a 90 giorni.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        {resultUrl && (
          <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/30 px-3 py-3 text-sm">
            <div className="font-semibold text-green-800 dark:text-green-300 mb-2">✅ Report pronto</div>
            <div className="flex items-center gap-2">
              <a href={resultUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700">
                <ExternalLink size={13} /> Apri PDF
              </a>
              <button onClick={copyUrl}
                className="inline-flex items-center gap-1.5 rounded-lg border border-green-300 dark:border-green-700 px-3 py-1.5 text-xs font-semibold text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copiato' : 'Copia link'}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={loading}>{inline ? 'Indietro' : 'Chiudi'}</Button>
          <Button onClick={generate} disabled={loading}>
            {loading
              ? (<span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Generazione… (1-3 min)</span>)
              : (<span className="inline-flex items-center gap-2"><FileText size={15} /> Genera report</span>)}
          </Button>
        </div>
      </div>
  );

  if (inline) return content;

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} size="md" title={`Report Social · ${clienteNome}`}>
      {content}
    </AnimatedModal>
  );
};

export default ReportGeneratorModal;
